import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const PILLARS_SL = [
  {
    number: '01',
    category: 'PRODAJNA KONVERZIJA',
    project: 'Union Nefiltrirano',
    client: 'Pivoarna Laško Union',
    accent: '#C87533',
    essence: 'Neposreden vpliv na nakupno odločitev na prodajnem mestu skozi izkušnjo okusa.',
    metrics: [
      { value: '50%',    label: 'povprečni dvig prodaje v aktiviranih trgovinah' },
      { value: '33%',    label: 'conversion rate — vsak 3. kupec se je odločil za nakup' },
      { value: '1.000+', label: 'izvedenih degustacij z edukacijo o nefiltriranem pivu' },
    ],
    highlight: 'Aktivna prodaja z argumenti o kakovosti in svežini.',
  },
  {
    number: '02',
    category: 'INDUSTRIJSKI STANDARD & SKLADNOST',
    project: 'DEKRA / JTI Tobačni Sektor',
    client: 'DEKRA (JTI)',
    accent: '#C87533',
    essence: 'Operativno delo znotraj najstrožje regulirane industrije z neposrednim razumevanjem tobačnega portfelja.',
    metrics: [
      { value: '0',    label: 'kršitev — Zero-Incident Compliance v celotnem obdobju projekta' },
      { value: '100%', label: 'skladnost z zakonodajo in internimi pravili naročnika' },
      { value: '∞',    label: 'logistična preciznost pri upravljanju promo opreme na več lokacijah' },
    ],
    highlight: '"Compliance = Security" — razumevanje, da skladnost pomeni varnost blagovne znamke.',
  },
  {
    number: '03',
    category: 'STROKOVNA KOMUNIKACIJA',
    project: 'HP PageWide B2B Kampanja',
    client: 'Hewlett Packard',
    accent: '#C87533',
    essence: 'Razlaga tehnološko naprednih produktov na razumljiv in strokoven način poslovnim uporabnikom.',
    metrics: [
      { value: '17%',      label: 'podjetij se je odločilo za nakup ali dolgoročni najem opreme' },
      { value: 'Try & Buy', label: 'brezplačne 1-tedenske predstavitve na lokacijah podjetij' },
      { value: 'GDPR',     label: 'usklajen lead generation in kvalifikacijski klicni center' },
    ],
    highlight: 'Promotorji so delovali kot tehnološki svetovalci — ne prodajalci.',
  },
  {
    number: '04',
    category: 'OPERATIVNA PRISOTNOST V HORECA',
    project: 'G3Spirits Brand Engagement',
    client: 'G3 Spirits',
    accent: '#C87533',
    essence: 'Obvladovanje dinamičnega HoReCa okolja z visoko frekvenco — premium interakcija na terenu.',
    metrics: [
      { value: '100+', label: 'ključnih HoReCa lokacij po celotni Sloveniji' },
      { value: '100%', label: 'skladnost pri postavljanju promo materiala in Perfect Serve ritualu' },
      { value: '100%', label: 'pokritost celotne Slovenije — Center, Zahod, Vzhod hkrati' },
    ],
    highlight: 'Terenska agilnost: prilagajanje glede na zasedenost lokalov in tip dogodka.',
  },
];

const PILLARS_EN = [
  {
    number: '01',
    category: 'SALES CONVERSION',
    project: 'Union Nefiltrirano',
    client: 'Pivoarna Laško Union',
    accent: '#C87533',
    essence: 'Direct influence on purchase decisions at the point of sale through a taste experience.',
    metrics: [
      { value: '50%',    label: 'average sales lift in activated stores during the promotion period' },
      { value: '33%',    label: 'conversion rate — every 3rd taster made a purchase' },
      { value: '1,000+', label: 'tastings conducted with education on unfiltered beer specifics' },
    ],
    highlight: 'Active selling with quality and freshness arguments.',
  },
  {
    number: '02',
    category: 'INDUSTRY STANDARD & COMPLIANCE',
    project: 'DEKRA / JTI Tobacco Sector',
    client: 'DEKRA (JTI)',
    accent: '#C87533',
    essence: 'Operational work within the most strictly regulated industry with direct understanding of the tobacco portfolio.',
    metrics: [
      { value: '0',    label: 'violations — Zero-Incident Compliance throughout the entire project' },
      { value: '100%', label: 'compliance with tobacco legislation and client internal rules' },
      { value: '∞',    label: 'logistical precision managing promo assets across multiple locations' },
    ],
    highlight: '"Compliance = Security" — understanding that compliance means brand safety.',
  },
  {
    number: '03',
    category: 'EXPERT COMMUNICATION',
    project: 'HP PageWide B2B Campaign',
    client: 'Hewlett Packard',
    accent: '#C87533',
    essence: 'Explaining technically advanced products clearly to business users.',
    metrics: [
      { value: '17%',      label: 'of companies decided to purchase or lease equipment after the demo' },
      { value: 'Try & Buy', label: 'free 1-week on-site equipment trials that build technology trust' },
      { value: 'GDPR',     label: 'compliant lead generation and qualification call centre' },
    ],
    highlight: 'Promoters acted as technology advisors — not salespeople.',
  },
  {
    number: '04',
    category: 'HORECA OPERATIONAL PRESENCE',
    project: 'G3Spirits Brand Engagement',
    client: 'G3 Spirits',
    accent: '#C87533',
    essence: 'Managing a high-frequency HoReCa environment — premium interaction in the field.',
    metrics: [
      { value: '100+', label: 'key HoReCa locations across Slovenia covered' },
      { value: '100%', label: 'compliance with promo material placement and Perfect Serve ritual' },
      { value: '100%', label: 'national coverage — Centre, West and East simultaneously' },
    ],
    highlight: 'Field agility: rapid adaptation to venue occupancy and event type.',
  },
];

const PillarCard = ({ pillar }) => (
  <div className="pillar-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col gap-4 hover:border-white/15 hover:bg-white/5 transition-all duration-300">

    {/* Top accent line */}
    <div className="absolute top-0 left-6 right-6 h-px rounded-full"
      style={{ background: `linear-gradient(90deg, ${pillar.accent}70, transparent)` }}
    />

    {/* Hover glow */}
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${pillar.accent}10 0%, transparent 70%)` }}
    />

    {/* Header */}
    <div className="relative flex items-start gap-3">
      <span
        className="font-mono text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0 mt-0.5"
        style={{ background: `${pillar.accent}18`, color: pillar.accent, border: `1px solid ${pillar.accent}35` }}
      >
        {pillar.number}
      </span>
      <div>
        <p className="font-mono text-xs text-white/35 uppercase tracking-widest mb-1">{pillar.category}</p>
        <h3 className="font-display text-base font-bold text-white leading-tight">{pillar.project}</h3>
        <p className="text-white/30 text-xs font-mono mt-0.5">{pillar.client}</p>
      </div>
    </div>

    {/* Essence */}
    <p className="relative text-white/50 text-sm leading-relaxed border-l border-white/10 pl-3">
      {pillar.essence}
    </p>

    {/* Metrics */}
    <div className="relative space-y-2.5 flex-1">
      {pillar.metrics.map((m, i) => (
        <div key={i} className="flex items-baseline gap-3">
          <span
            className="font-display text-xl font-black leading-none flex-shrink-0 w-20 text-right"
            style={{ color: pillar.accent }}
          >
            {m.value}
          </span>
          <span className="text-white/40 text-xs leading-relaxed">{m.label}</span>
        </div>
      ))}
    </div>

    {/* Highlight */}
    <p className="relative text-white/30 text-xs leading-relaxed pt-3 border-t border-white/6">
      {pillar.highlight}
    </p>

    {/* Bottom hover line */}
    <div
      className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full"
      style={{ background: pillar.accent }}
    />
  </div>
);

export const CaseStudiesSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const PILLARS = lang === 'sl' ? PILLARS_SL : PILLARS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.fromTo('.cs-header',
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true } }
      );
      gsap.fromTo('.pillar-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.65, ease: 'power3.out', stagger: 0.12,
          scrollTrigger: { trigger: '.pillars-grid', start: 'top 75%', once: true },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="case-studies"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Header */}
        <div className="cs-header text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
              {lang === 'sl' ? 'Primeri projektov' : 'Project Case Studies'}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {lang === 'sl'
              ? <>Strateški stebri naše<br/><span className="text-[#C87533]">operativne odličnosti</span></>
              : <>Strategic pillars of our<br/><span className="text-[#C87533]">operational excellence</span></>
            }
          </h2>
          <p className="text-white/40 max-w-xl mx-auto text-base leading-relaxed">
            {lang === 'sl'
              ? 'Dokazane kompetence skozi konkretne projekte — od maloprodaje do reguliranih industrij.'
              : 'Proven competencies through concrete projects — from retail to regulated industries.'}
          </p>
        </div>

        {/* 2×2 grid */}
        <div className="pillars-grid grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map((pillar) => (
            <PillarCard key={pillar.number} pillar={pillar} />
          ))}
        </div>

      </div>
    </section>
  );
};
